import React from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Grid,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutline as ErrorOutlineIcon,
  HelpOutline as HelpOutlineIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { LocationConfig, Circle, Stage } from './types/AccuLynxTypes';
import { LocationCard as StyledLocationCard } from './styles/AccuLynxStyles';
import { StageMappings } from './StageMappings';

interface LocationCardProps {
  location: LocationConfig;
  locationIndex: number;
  isEditing: boolean;
  isLoadingCircles: boolean;
  isLoadingStages: boolean;
  circles: Circle[];
  paivStages: Stage[];
  stageCategories: any[]; // new prop
  circleLevels?: { id: string; name: string }[]; // levels list
  hideCrmCategory?: boolean;
  onUpdateLocation: (
    index: number,
    field: keyof LocationConfig,
    value: any
  ) => void;
  onApiKeyChange: (index: number, value: string) => void;
  onRemoveLocation?: (index: number) => void;
  onUpdateStageMapping: (
    locationIndex: number,
    accuLynxStage: string,
    paivStageTitle: string
  ) => void;
}

export const LocationCard: React.FC<LocationCardProps> = ({
  location,
  locationIndex,
  isEditing,
  isLoadingCircles,
  isLoadingStages,
  circles,
  paivStages,
  stageCategories,
  circleLevels = [],
  hideCrmCategory = false,
  onUpdateLocation,
  onApiKeyChange,
  onRemoveLocation,
  onUpdateStageMapping,
}) => {
  const [showApiKey, setShowApiKey] = React.useState(false);

  const filteredCircles = Array.isArray(circles)
    ? circles.filter(
        (c) =>
          c?.levelID === location?.circleLevelID && c?.isActive && !c?.isDeleted
      )
    : [];

  return (
    <StyledLocationCard>
      <Box sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="subtitle1">
            Location {locationIndex + 1}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* X button to remove location - only show on locations after the first one, when editing */}
            {/* Show if: editing AND (there's a saved integration OR we're adding new locations) AND not the first location */}
            {onRemoveLocation && isEditing && locationIndex > 0 && (
              <Tooltip title="Remove this location">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onRemoveLocation(locationIndex)}
                  sx={{
                    width: 32,
                    height: 32,
                    border: '1px solid',
                    borderColor: 'error.main',
                    '&:hover': {
                      backgroundColor: 'error.main',
                      color: 'error.contrastText',
                      borderColor: 'error.dark',
                    },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Grid container spacing={2}>
          {/* Level (first question) */}
          <Grid item xs={12} md={6}>
            {/* Label + help */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', mr: 0.5 }}
              >
                Level
              </Typography>
              <Tooltip
                title="Choose the Paiv circle level to organize this integration (e.g., Company, Region, Team). We'll show only locations at this level."
                placement="right"
              >
                <Box
                  component="span"
                  sx={{ display: 'inline-flex', alignItems: 'center' }}
                  aria-label="What is Level?"
                >
                  <HelpOutlineIcon
                    sx={{ fontSize: 14, color: 'text.disabled' }}
                  />
                </Box>
              </Tooltip>
            </Box>
            <FormControl fullWidth>
              <Select
                value={location?.circleLevelID || ''}
                onChange={(e) => {
                  onUpdateLocation(
                    locationIndex,
                    'circleLevelID',
                    e.target.value
                  );
                  // Clear any previously selected location when level changes
                  onUpdateLocation(locationIndex, 'circleId', '');
                }}
                disabled={!isEditing}
                sx={{
                  backgroundColor: '#ffffff',
                  '& .MuiSelect-select': {
                    backgroundColor: '#ffffff',
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    elevation: 8,
                    sx: {
                      backgroundColor: '#ffffff !important',
                      '& .MuiList-root': {
                        backgroundColor: '#ffffff !important',
                        padding: '8px 0',
                      },
                      '& .MuiMenuItem-root': {
                        backgroundColor: '#ffffff !important',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(0, 0, 0, 0.08) !important',
                        },
                      },
                    },
                  },
                }}
              >
                {circleLevels.length === 0 ? (
                  <MenuItem disabled>Loading levels...</MenuItem>
                ) : (
                  circleLevels.map((lvl) => (
                    <MenuItem key={lvl.id} value={lvl.id}>
                      {lvl.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>

          {/* Location filtered by selected level */}
          <Grid item xs={12} md={6}>
            {/* Label + help */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', mr: 0.5 }}
              >
                Location
              </Typography>
              <Tooltip
                title="Select the specific Paiv location (team/site) at the chosen level to connect with this AccuLynx API key."
                placement="right"
              >
                <Box
                  component="span"
                  sx={{ display: 'inline-flex', alignItems: 'center' }}
                  aria-label="What is Location?"
                >
                  <HelpOutlineIcon
                    sx={{ fontSize: 14, color: 'text.disabled' }}
                  />
                </Box>
              </Tooltip>
            </Box>
            <FormControl fullWidth>
              <Select
                value={location?.circleId || ''}
                onChange={(e) =>
                  onUpdateLocation(locationIndex, 'circleId', e.target.value)
                }
                disabled={
                  !isEditing || isLoadingCircles || !location?.circleLevelID
                }
                sx={{
                  backgroundColor: '#ffffff',
                  '& .MuiSelect-select': {
                    backgroundColor: '#ffffff',
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    elevation: 8,
                    sx: {
                      backgroundColor: '#ffffff !important',
                      '& .MuiList-root': {
                        backgroundColor: '#ffffff !important',
                        padding: '8px 0',
                      },
                      '& .MuiMenuItem-root': {
                        backgroundColor: '#ffffff !important',
                        '&:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                        },
                        '&.Mui-selected': {
                          backgroundColor: 'rgba(0, 0, 0, 0.08) !important',
                        },
                      },
                    },
                  },
                }}
                renderValue={(value) => {
                  const id = String(value || '');
                  const match = filteredCircles.find((c) => c.id === id);
                  const resolved = match?.name || location?.circleName || '';
                  return resolved;
                }}
              >
                {isLoadingCircles ? (
                  <MenuItem disabled>Loading locations...</MenuItem>
                ) : !location?.circleLevelID ? (
                  <MenuItem disabled>Select a Level first</MenuItem>
                ) : filteredCircles.length === 0 ? (
                  <MenuItem disabled>No locations found</MenuItem>
                ) : (
                  filteredCircles.map((circle) => (
                    <MenuItem key={circle.id} value={circle.id}>
                      {circle.name}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>

          {/* CRM Category */}
          {!hideCrmCategory && (
            <Grid item xs={12} md={6}>
              {/* Label + help */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', mr: 0.5 }}
                >
                  CRM Category
                </Typography>
                <Tooltip
                  title="Pick which CRM category's stages to map for this location. Most orgs use Contacts as the default. Choose another only if your locations use different stage sets."
                  placement="right"
                >
                  <Box
                    component="span"
                    sx={{ display: 'inline-flex', alignItems: 'center' }}
                    aria-label="What is CRM Category?"
                  >
                    <HelpOutlineIcon
                      sx={{ fontSize: 14, color: 'text.disabled' }}
                    />
                  </Box>
                </Tooltip>
              </Box>
              <FormControl fullWidth>
                <Select
                  value={location?.stageCategoryID || ''}
                  onChange={(e) =>
                    onUpdateLocation(
                      locationIndex,
                      'stageCategoryID',
                      e.target.value
                    )
                  }
                  disabled={!isEditing}
                  sx={{
                    backgroundColor: '#ffffff',
                    '& .MuiSelect-select': {
                      backgroundColor: '#ffffff',
                    },
                  }}
                  MenuProps={{
                    PaperProps: {
                      elevation: 8,
                      sx: {
                        backgroundColor: '#ffffff !important',
                        '& .MuiList-root': {
                          backgroundColor: '#ffffff !important',
                          padding: '8px 0',
                        },
                        '& .MuiMenuItem-root': {
                          backgroundColor: '#ffffff !important',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'rgba(0, 0, 0, 0.08) !important',
                          },
                        },
                      },
                    },
                  }}
                >
                  {stageCategories && stageCategories.length > 0 ? (
                    stageCategories.map((cat: any) => (
                      <MenuItem key={cat.id} value={cat.id}>
                        {cat.title}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No categories</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            {/* Label + help */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', mr: 0.5 }}
              >
                API Key
              </Typography>
              <Tooltip
                title="Paste the AccuLynx API key for this location. Each AccuLynx location should have its own key."
                placement="right"
              >
                <Box
                  component="span"
                  sx={{ display: 'inline-flex', alignItems: 'center' }}
                  aria-label="What is API Key?"
                >
                  <HelpOutlineIcon
                    sx={{ fontSize: 14, color: 'text.disabled' }}
                  />
                </Box>
              </Tooltip>
            </Box>
            <Box sx={{ position: 'relative' }}>
              <TextField
                placeholder="API Key"
                type="text"
                required
                value={
                  showApiKey || !location?.apiKey
                    ? location?.apiKey || ''
                    : '•'.repeat(location.apiKey.length || 8)
                }
                onChange={(e) => {
                  // Only update if showing the actual value
                  if (showApiKey) {
                    onApiKeyChange(locationIndex, e.target.value);
                  } else {
                    // If masked, reveal it first, then update
                    setShowApiKey(true);
                    onApiKeyChange(locationIndex, e.target.value);
                  }
                }}
                onFocus={(e) => {
                  // When focused, if masked, reveal it
                  if (!showApiKey && location?.apiKey) {
                    setShowApiKey(true);
                    // Set cursor to end
                    setTimeout(() => {
                      e.target.setSelectionRange(
                        e.target.value.length,
                        e.target.value.length
                      );
                    }, 0);
                  }
                }}
                error={location?.connectionStatus === 'failed'}
                disabled={!isEditing}
                fullWidth
                autoComplete="off"
                name={`acculynx-api-key-field-${locationIndex}`}
                id={`acculynx-api-key-input-${locationIndex}`}
                inputProps={{
                  autoComplete: 'off',
                  'data-form-type': 'other',
                  'data-lpignore': 'true',
                  'data-1p-ignore': 'true',
                  spellCheck: 'false',
                }}
                InputProps={{
                  endAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {location?.connectionStatus === 'testing' && (
                        <CircularProgress size={20} />
                      )}
                      {location?.connectionStatus === 'connected' && (
                        <CheckCircleOutlineIcon
                          sx={{ color: 'success.main', fontSize: 22 }}
                        />
                      )}
                      {location?.connectionStatus === 'failed' && (
                        <ErrorOutlineIcon
                          sx={{ color: 'error.main', fontSize: 22 }}
                        />
                      )}
                      {isEditing && location?.apiKey && (
                        <IconButton
                          size="small"
                          onClick={() => setShowApiKey(!showApiKey)}
                          edge="end"
                          sx={{ p: 0.5 }}
                        >
                          {showApiKey ? (
                            <VisibilityOffIcon fontSize="small" />
                          ) : (
                            <VisibilityIcon fontSize="small" />
                          )}
                        </IconButton>
                      )}
                    </Box>
                  ),
                }}
              />
              {location?.connectionError && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ mt: 1, display: 'block' }}
                >
                  {location.connectionError}
                </Typography>
              )}
              {location?.connectionStatus === 'connected' && (
                <Typography
                  color="success.main"
                  variant="caption"
                  sx={{ mt: 1, display: 'block' }}
                >
                  Successfully connected to AccuLynx
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Stage Mappings */}
        <StageMappings
          location={location}
          locationIndex={locationIndex}
          isEditing={isEditing}
          isLoadingStages={isLoadingStages}
          paivStages={paivStages}
          onUpdateStageMapping={onUpdateStageMapping}
        />
      </Box>
    </StyledLocationCard>
  );
};
